import type React from 'react';
import { useState, useCallback, useEffect } from 'react';

import { TextField as MuiTextField } from '@mui/material';
import { dataSchema } from '@ragu-components/ord-types';
import { useReactFlow } from '@xyflow/react';
import { TextField, useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import ResourceList from '../../components/ResourceList';
import SortableInstructionsList from '../../components/SortableInstructionsList';
import NodeControlForm from '../NodeControlForm';

import type { CallflowNodeInstruction, ExtensionInstruction, NodeControlsProps } from '../../dataProvider/types';
import type { GroupRead } from '@ragu-components/ord-types';

export interface GroupType extends Record<string, unknown> {
  group: GroupRead | null;
  call_timeout: number | null;
  instructions: ExtensionInstruction[];
}

interface GroupControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: GroupType;
}

export default function GroupControls({ updateNodeData, data }: GroupControlsProps): React.ReactElement {
  const translate = useTranslate();
  const reactFlowInstance = useReactFlow();

  const [group, setGroup] = useState<GroupRead | null>(data.group ?? null);
  const [callTimeout, setCallTimeout] = useState<number>(data.call_timeout ?? 30);
  const [instructions, setInstructions] = useState<ExtensionInstruction[]>(data.instructions ?? []);

  const removeEdgesByHandles = useCallback(
    (handles: string[]) => {
      if (handles.length === 0) return;
      const edges = reactFlowInstance?.getEdges();
      if (edges === undefined) return;
      const filteredEdges = edges.filter((edge) => {
        if (edge.sourceHandle === undefined || edge.sourceHandle === null) {
          return true;
        }
        return !handles.includes(edge.sourceHandle);
      });
      if (filteredEdges.length !== edges.length) {
        flowchartLogger.debug('GroupControls: deleting edges', { handles });
        reactFlowInstance?.setEdges(filteredEdges);
      }
    },
    [reactFlowInstance]
  );

  const onGroupDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('GroupControls: onGroupDataUpdate', { group });
    if (group === undefined || group === null) {
      flowchartLogger.debug('GroupControls: group is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ group, call_timeout: callTimeout, instructions });
  };

  const updateIfValid = useCallback((): void => {
    if (
      // Have to check for null to avoid TS errors
      instructions === null ||
      instructions.length === 0 ||
      instructions.find(
        (instruction) => instruction.match_exit_causes === null || instruction.match_exit_causes.length === 0
      ) !== undefined
    ) {
      flowchartLogger.debug('ExtensionControls: invalid form data, update cancelled');
      return;
    }
    flowchartLogger.debug('ExtensionControls: node data updated');
    updateNodeData({ instructions, call_timeout: callTimeout, group });
  }, [instructions, updateNodeData, callTimeout, group]);

  const handleInstructionDelete = useCallback(
    (handleId: string) => {
      flowchartLogger.debug('GroupControls: removing instruction handle', { handleId });
      removeEdgesByHandles([handleId]);
      updateIfValid();
    },
    [removeEdgesByHandles, updateIfValid]
  );

  useEffect(() => {
    flowchartLogger.debug('GroupControls: group changed', { group });
    updateNodeData({ group, call_timeout: callTimeout, instructions });
  }, [group, callTimeout, updateNodeData, instructions]);

  return (
    <NodeControlForm
      onSubmit={onGroupDataUpdate}
      isDisabled={group === undefined || group === null}
      data={data}
      updateNodeData={updateNodeData}
    >
      <MuiTextField
        value={callTimeout}
        label={translate('callflows.nodes.groupNode.callTimeout')}
        type='number'
        slotProps={{ htmlInput: { min: 10 } }}
        onChange={(event) => {
          let value = parseInt(event.target.value);
          if (isNaN(value)) {
            flowchartLogger.debug('GroupControls: call_timeout is NaN, setting to 30', { value });
            value = 30;
          }
          setCallTimeout(value);
        }}
      />
      <SortableInstructionsList
        inputs={instructions}
        onChange={setInstructions as React.Dispatch<React.SetStateAction<CallflowNodeInstruction[]>>}
        onDelete={handleInstructionDelete}
        matchExitCauses={dataSchema.CallflowGroup.properties.connections.items.properties.match_exit_causes.items.enum.map(
          (choice) => choice
        )}
      />

      <ResourceList
        resource='groups'
        selectedResourceId={group !== null ? group.id : null}
        onRowClick={(_id, _resource, record) => {
          flowchartLogger.debug('GroupControls: onRowClick', { _id });
          setGroup(record as GroupRead);
          return false;
        }}
      >
        <TextField source='name' />
        <TextField source='description' />
      </ResourceList>
    </NodeControlForm>
  );
}
